import React, { useState, useEffect } from 'react';
import { X, BookOpen } from 'lucide-react';
import { apiCall, generateId, formatPrice, formatDate } from '../utils/utils.js';

const SupplierLedgerModal = ({ supplier, onClose, onPay, onBill }) => {
    const [filterFrom, setFilterFrom] = useState('');
    const [filterTo,   setFilterTo]   = useState('');
    const [txns,       setTxns]       = useState([]);
    const [loading,    setLoading]    = useState(true);
    const [billAmt,    setBillAmt]    = useState('');
    const [payAmt,     setPayAmt]     = useState('');
    const [note,       setNote]       = useState('');
    const [saving,     setSaving]     = useState(false);

    const load = async () => {
        if (!supplier?.id) return;
        setLoading(true);
        const res = await apiCall('getSupplierTransactions', { supplier_id: supplier.id });
        if (Array.isArray(res)) setTxns(res);
        setLoading(false);
    };

    useEffect(() => { load(); }, [supplier?.id]);

    if (!supplier) return null;

    const handleBill = async () => {
        if (!billAmt || isNaN(billAmt)) return;
        setSaving(true);
        await apiCall('addSupplierTransaction', {
            id: generateId(), supplier_id: supplier.id,
            type: 'Purchase', amount: parseFloat(billAmt),
            note: note || 'Purchase / Invoice',
        });
        onBill && onBill(supplier.id, parseFloat(billAmt), 'Purchase');
        setBillAmt(''); setNote('');
        await load();
        setSaving(false);
    };

    const handlePay = async () => {
        if (!payAmt || isNaN(payAmt)) return;
        setSaving(true);
        await apiCall('addSupplierTransaction', {
            id: generateId(), supplier_id: supplier.id,
            type: 'Payment', amount: parseFloat(payAmt),
            note: note || 'Payment Sent',
        });
        onPay && onPay(supplier.id, parseFloat(payAmt), 'Payment');
        setPayAmt(''); setNote('');
        await load();
        setSaving(false);
    };

    // Filter + running balance
    const filtered = txns.filter(t => {
        const d = (t.created_at || '').replace(' ','T');
        if (filterFrom && new Date(d) < new Date(`${filterFrom}T00:00:00`)) return false;
        if (filterTo   && new Date(d) > new Date(`${filterTo}T23:59:59`))   return false;
        return true;
    });

    let running = 0;
    const rows = filtered.map(t => {
        const debit  = t.type === 'Purchase' ? parseFloat(t.amount) : 0;
        // Both Payment and Return reduce the outstanding balance
        const credit = (t.type === 'Payment' || t.type === 'Return') ? parseFloat(t.amount) : 0;
        running += debit - credit;
        return { ...t, debit, credit, balance: running };
    });

    const totalBilled = rows.reduce((a,r) => a + r.debit,  0);
    const totalPaid   = rows.reduce((a,r) => a + r.credit, 0);
    const balance     = totalBilled - totalPaid;

    return (
        <div className="fixed inset-0 bg-[#1a1a1a]/60 backdrop-blur-sm z-[100] flex items-center justify-center p-2 sm:p-4 animate-in fade-in duration-200">
            <div className="bg-white w-full max-w-2xl rounded-xl shadow-2xl border border-[#e1e3e5] flex flex-col max-h-[95vh]">
                {/* Header */}
                <div className="p-4 border-b border-[#e1e3e5] bg-[#f6f7f8] flex justify-between items-start">
                    <div>
                        <h3 className="font-black text-[#1a1a1a] flex items-center gap-2"><BookOpen size={18} className="text-[#b98900]"/> {supplier.name} — Supplier Ledger</h3>
                        <p className="text-xs text-[#5c5f62] mt-0.5">{supplier.phone}</p>
                    </div>
                    <button onClick={onClose}><X size={20} className="text-[#5c5f62]"/></button>
                </div>

                {/* Summary */}
                <div className="grid grid-cols-3 divide-x divide-[#e1e3e5] border-b border-[#e1e3e5]">
                    <div className="p-3 text-center"><p className="text-[10px] font-bold text-[#5c5f62] uppercase">Total Billed</p><p className="font-black text-[#d82c0d] text-base">{formatPrice(totalBilled)}</p></div>
                    <div className="p-3 text-center"><p className="text-[10px] font-bold text-[#5c5f62] uppercase">Total Paid</p><p className="font-black text-[#008060] text-base">{formatPrice(totalPaid)}</p></div>
                    <div className="p-3 text-center">
                        <p className="text-[10px] font-bold text-[#5c5f62] uppercase">You Owe</p>
                        <p className={`font-black text-base ${balance > 0 ? 'text-[#d82c0d]' : 'text-[#008060]'}`}>{formatPrice(Math.abs(balance))}</p>
                        <p className="text-[8px] font-bold uppercase text-[#5c5f62]">{balance > 0 ? 'Payable' : 'Clear'}</p>
                    </div>
                </div>

                {/* Add bill / payment row */}
                <div className="p-3 border-b border-[#e1e3e5] bg-white flex flex-wrap gap-2 items-end">
                    <div className="flex flex-col gap-1 flex-1 min-w-[110px]">
                        <label className="text-[10px] font-bold text-[#5c5f62] uppercase">Note (optional)</label>
                        <input value={note} onChange={e=>setNote(e.target.value)} placeholder="Invoice / payment ref" className="p-1.5 border border-[#c9cccf] rounded-lg text-xs outline-none w-full"/>
                    </div>
                    <div className="flex gap-2 items-end">
                        <div className="flex flex-col gap-1">
                            <label className="text-[10px] font-bold text-[#d82c0d] uppercase">+ Bill (Rs)</label>
                            <input type="number" value={billAmt} onChange={e=>setBillAmt(e.target.value)} placeholder="0" className="p-1.5 border border-[#d82c0d]/40 rounded-lg text-xs outline-none w-24 text-center font-bold"/>
                        </div>
                        <button onClick={handleBill} disabled={!billAmt || saving} className="px-3 py-1.5 bg-[#d82c0d] text-white rounded-lg text-xs font-bold disabled:opacity-40 hover:bg-[#b82009]">+ Add</button>
                    </div>
                    <div className="flex gap-2 items-end">
                        <div className="flex flex-col gap-1">
                            <label className="text-[10px] font-bold text-[#008060] uppercase">- Pay (Rs)</label>
                            <input type="number" value={payAmt} onChange={e=>setPayAmt(e.target.value)} placeholder="0" className="p-1.5 border border-[#008060]/40 rounded-lg text-xs outline-none w-24 text-center font-bold"/>
                        </div>
                        <button onClick={handlePay} disabled={!payAmt || saving} className="px-3 py-1.5 bg-[#008060] text-white rounded-lg text-xs font-bold disabled:opacity-40 hover:bg-[#006e52]">Pay</button>
                    </div>
                </div>

                {/* Date filter */}
                <div className="px-3 py-2 border-b border-[#e1e3e5] bg-[#f6f7f8] flex gap-2 items-center text-xs">
                    <span className="font-semibold text-[#5c5f62]">Filter:</span>
                    <input type="date" value={filterFrom} onChange={e=>setFilterFrom(e.target.value)} className="p-1 border border-[#c9cccf] rounded text-xs outline-none"/>
                    <span className="text-[#5c5f62]">→</span>
                    <input type="date" value={filterTo} onChange={e=>setFilterTo(e.target.value)} className="p-1 border border-[#c9cccf] rounded text-xs outline-none"/>
                    {(filterFrom||filterTo) && <button onClick={()=>{setFilterFrom('');setFilterTo('');}} className="text-[#d82c0d] font-bold text-[10px]">✕ Clear</button>}
                </div>

                {/* Ledger table */}
                <div className="overflow-y-auto flex-1">
                    {loading ? (
                        <div className="text-center py-12 text-[#5c5f62] text-sm">Loading transactions…</div>
                    ) : rows.length === 0 ? (
                        <div className="text-center py-12 text-[#5c5f62]">
                            <BookOpen size={36} className="mx-auto mb-3 opacity-40"/>
                            <p className="text-sm">No transactions yet. Add a bill or payment above.</p>
                        </div>
                    ) : (
                        <table className="w-full text-xs">
                            <thead className="sticky top-0 bg-[#f6f7f8] border-b border-[#e1e3e5]">
                                <tr>
                                    <th className="p-3 text-left font-bold text-[#5c5f62] uppercase">Date</th>
                                    <th className="p-3 text-left font-bold text-[#5c5f62] uppercase">Description</th>
                                    <th className="p-3 text-right font-bold text-[#d82c0d] uppercase">Bill (Dr)</th>
                                    <th className="p-3 text-right font-bold text-[#008060] uppercase">Payment (Cr)</th>
                                    <th className="p-3 text-right font-bold text-[#1a1a1a] uppercase">Balance</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-[#f6f7f8]">
                                {rows.map((r,i) => (
                                    <tr key={i} className={r.type === 'Payment' ? 'bg-[#f0fdf4]' : r.type === 'Return' ? 'bg-[#f5f3ff]' : 'hover:bg-[#fafafa]'}>
                                        <td className="p-3 text-[#5c5f62] whitespace-nowrap">{formatDate(r.created_at)}</td>
                                        <td className="p-3 font-medium text-[#1a1a1a]">{r.note || r.type}</td>
                                        <td className="p-3 text-right font-bold text-[#d82c0d]">{r.debit > 0 ? formatPrice(r.debit) : '—'}</td>
                                        <td className="p-3 text-right font-bold text-[#008060]">{r.credit > 0 ? formatPrice(r.credit) : '—'}</td>
                                        <td className={`p-3 text-right font-black ${r.balance > 0 ? 'text-[#d82c0d]' : 'text-[#008060]'}`}>{formatPrice(Math.abs(r.balance))} {r.balance > 0 ? 'Dr' : 'Cr'}</td>
                                    </tr>
                                ))}
                            </tbody>
                            <tfoot className="sticky bottom-0 bg-[#1a1a1a] text-white">
                                <tr>
                                    <td className="p-3 font-black uppercase" colSpan={2}>Totals</td>
                                    <td className="p-3 text-right font-black text-[#fca5a5]">{formatPrice(totalBilled)}</td>
                                    <td className="p-3 text-right font-black text-[#86efac]">{formatPrice(totalPaid)}</td>
                                    <td className={`p-3 text-right font-black ${balance > 0 ? 'text-[#fca5a5]' : 'text-[#86efac]'}`}>{formatPrice(Math.abs(balance))} {balance > 0 ? 'Dr' : 'Cr'}</td>
                                </tr>
                            </tfoot>
                        </table>
                    )}
                </div>
            </div>
        </div>
    );
};

export default SupplierLedgerModal;
